import { Text } from '@lmn/design-system';
import styled, { css } from 'styled-components';
import customCss from '../css/customCss';

// The gutters an evo section keeps from the viewport edge below lg, where the grid column takes
// over. Split by property because the two carriers need different halves: EvoSection is used on
// its own by elements that bring their own padding, EvoSectionContainer adds the inner one.
// Left/right rather than the `margin: 0 24px` shorthand: a gutter has no business resetting the
// vertical margins of whatever it is applied to (the shorthand silently ate the bottom ad's 16px
// separation between its two slots, and left the stepper's margin-bottom depending on rule order).
const gutterMargin = css`
  @media${customCss.breakpoints.sm} {
    margin-left: 24px;
    margin-right: 24px;
  }

  @media ${customCss.breakpoints.lg} {
    margin-left: 0;
    margin-right: 0;
  }
`;

const gutterPadding = css`
  padding: 0 16px;

  @media${customCss.breakpoints.sm} {
    padding: 0;
  }
`;

/**
 * Both halves together — 16px, 24px from sm, none from lg — for anything outside this file that
 * has to line up with the evo sections (the bottom ad). Consume this instead of re-stating the
 * numbers, so the sections and everything aligned to them can never drift apart.
 */
export const evoSectionGutters = css`
  ${gutterPadding}
  ${gutterMargin}
`;

export const EvoSection = styled.div`
  ${gutterMargin}
`;

export const EvoSectionContainer = styled(EvoSection)`
  display: flex;
  flex-direction: column;
  background-color: white;
  gap: 24px;

  ${gutterPadding}
`;

/**
 * The same section rendered as a tab block of the Evo hotel-info modal, where it sits alongside
 * the HotelPageContentSection-based sections and has to read like them: the title-to-content gap
 * follows the tab's own rhythm instead of a flat 24px: space150 for mobile and tablet alike, then
 * space300 from lg. There is no tablet step, so the switch is at lg rather than sm.
 */
export const EvoModalSectionContainer = styled(EvoSectionContainer)`
  gap: ${({ theme }) => theme.kallisto.space.space150};

  @media ${customCss.breakpoints.lg} {
    gap: ${({ theme }) => theme.kallisto.space.space300};
  }
`;

/**
 * Heading of an evo section. On the page it is the large Title2; as a modal tab block it matches
 * the tab's other section titles (see HotelPageContentSection's Title: Title3, Title2 from xl).
 */
export const EvoSectionHeading = styled(Text).attrs({ variant: 'Title2' })<{ $inModal?: boolean }>`
  ${({ theme, $inModal }) =>
    $inModal &&
    css`
      ${theme.kallisto.mixins.font('Title3')};

      @media ${customCss.breakpoints.xl} {
        ${theme.kallisto.mixins.font('Title2')};
      }
    `}
`;
